import './launchpad-embed-reset';
import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { KeycloakGate } from './auth/KeycloakGate';
import { installChunkPreloadErrorRegistry } from './lib/chunk-load-failure';
import { queryClient } from './lib/query-client';
import { installOfflineDetector } from './lib/offline/install-offline-detector';
import { OfflineGate } from './lib/offline/OfflineGate';
import { router } from './router/router';

const RouterDevtoolsLazy = import.meta.env.DEV
  ? lazy(() => import('./router/router-devtools').then((m) => ({ default: m.RouterDevtools })))
  : () => null;

const QueryDevtoolsLazy = import.meta.env.DEV
  ? lazy(() => import('./lib/query-devtools').then((m) => ({ default: m.QueryDevtools })))
  : () => null;
import { Toaster } from '@eureca/ui';
import { setBaseUrl } from '@eureca/candidate-api-sdk/client';
import { captureEurecaGtmRegistrationContextFromSearch } from './auth/eureca-gtm-registration-params';
import { candidateEnv } from './config/env';
import { captureFbclid } from './datalayer-meta';
import { initGtm } from './gtm';
import { initVLibras } from './vlibras';
import { initFloatingLaunchers } from './floating-launchers';
import './index.css';

// Point the SDK at the runtime-resolved BFF host (eureca.dev vs eureca.me); the
// SDK otherwise reads the build-time VITE_API_URL. @see config/env.ts
setBaseUrl(candidateEnv.apiUrl);

captureEurecaGtmRegistrationContextFromSearch();
document.title = candidateEnv.appDisplayName;
// EU-392 — `initGtm` inicializa `window.dataLayer` antes de injetar o container.
initGtm();
// EU-587 — persiste o fbclid do anúncio como cookie de primeira parte (_fbc)
// no boot: o lead pode concluir em outra página e as tags de conversão
// (sign_up/generate_lead) ainda encontram o fbc para o Advanced Matching.
captureFbclid();
// EU-506 — o código de monitoramento do RD Station NÃO é injetado aqui: ele é
// carimbado no <head> do index.html em build time (vite-rd-station-plugin.ts),
// porque o verificador do RD lê o HTML estático e não executa JS.
// EU-574 — widget de acessibilidade VLibras (Português → LIBRAS) em todas as
// páginas; carregamento assíncrono e resiliente à indisponibilidade do gov.br.
initVLibras();
// DEV-648 — botões flutuantes de terceiros (suporte e VLibras) acima da barra de
// ações dos formulários: observa o DOM e publica a reserva no <html> (CSS em index.css).
initFloatingLaunchers();
// EU-711 — ponto ÚNICO de "sem internet" do app. Observa o transporte do SDK
// (por onde passam todas as superfícies) e alimenta o gate abaixo. Antes, a
// falha de conexão numa mutation só produzia um toast que sumia sozinho.
installOfflineDetector(queryClient);
// EU-747 — registra os erros que o Vite viu falhar no `import()` de chunk
// (`vite:preloadError`). O `RouterErrorScreen` trata como "chunk não chegou"
// qualquer erro registrado aqui, independente da mensagem do engine. Não
// recarrega nem cancela o evento — ver docblock em `lib/chunk-load-failure.ts`.
installChunkPreloadErrorRegistry();

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root).render(
  <StrictMode>
    <KeycloakGate>
      <QueryClientProvider client={queryClient}>
        {/* EU-711 — sobrepõe a tela de sem-internet mantendo a árvore MONTADA:
            é o que preserva o preenchimento da etapa ao voltar (FR-007). */}
        <OfflineGate>
          <RouterProvider router={router} context={{ query: queryClient }} />
        </OfflineGate>
        {/* Sem override de posição — herda o `bottom-right` default do design
            system, alinhando os toasts do candidato aos apps recruiter e
            backoffice (padrão da plataforma). */}
        <Toaster />
        {import.meta.env.DEV ? (
          <Suspense fallback={null}>
            <RouterDevtoolsLazy />
            <QueryDevtoolsLazy />
          </Suspense>
        ) : null}
      </QueryClientProvider>
    </KeycloakGate>
  </StrictMode>
);
